import React, { useCallback, useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { TFunction, useTranslation } from '@lyearn/i18n';
import { AddBlock } from '@lyearn/molecules';
import { Stack } from '@lyearn/ui';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import DraggableList from '@/modules/corporate/components/DraggableList';
import YellowWarning from '@/modules/corporate/modules/questions/fields/components/YellowWarning';
import useEditorOptionsList from '@/modules/corporate/modules/questions/fields/hooks/useEditorOptionsList';
import { QuestionEditorProps } from '@/modules/corporate/modules/questions/fields/types';
import { QuestionSubTypeEnum } from '@/types/schema';

import AIAddBlock from '../../components/AddBlock';
import AIOptionsSuggestions from '../../components/AIOptionsSuggestions';
import { addOptionStyles } from '../../components/Editor/styles';
import EditorOptionsListItem from './components/EditorOptionsListItem';

const CLASSES = { itemDropOverContainer: '-ms-24' };

/**
 * Validates weighted option questions for edge cases:
 * - Multiple options with 100% weight
 * - No option with 100% weight
 * Returns warning or null if validation passes
 */
const validateWeightedOptions = (t: TFunction, options: any[]) => {
  if (!options || options.length === 0) {
    return null;
  }

  const optionsWithMaxWeight = options.filter((option) => option.optionRelativeScore === 100);
  const optionsWithAnyWeight = options.filter((option) => (option.optionRelativeScore ?? 0) > 0);

  // Warning: Multiple options have 100% weight
  if (optionsWithMaxWeight.length > 1) {
    return {
      message: t('QuizCreator:Validations.MultipleMaxWeightedOptions'),
      type: 'MultipleMaxWeightedOptions',
    };
  }

  // Warning: No option has 100% weight (all are below 100 or 0)
  if (optionsWithAnyWeight.length > 0 && optionsWithMaxWeight.length === 0) {
    return {
      message: t('QuizCreator:Validations.NoMaxWeightedOption'),
      type: 'NoMaxWeightedOption',
    };
  }

  return null;
};

function EditorOptionsList(props: QuestionEditorProps) {
  const {
    viewMode,
    className,
    question,
    setNudgeState,
    onChange,
    isMultiSelect,
    variant = 'quiz',
    isLocalizedContent,
  } = props;
  const { t } = useTranslation();
  const [showAISuggestions, { on: openAISuggestions, off: closeAISuggestions }] = useToggle(false);
  const { options, onAction, onDrop, onAddOption, onAddSuggestions } = useEditorOptionsList({
    question,
    onChange,
    isMultiSelect,
    variant,
  });

  const disableReordering =
    variant === 'survey' || Boolean(question.settings?.shuffleOptions) || viewMode == 'view';
  const allValidOptions = options.filter((option) => option.optionText);
  const allCorrectOptions = allValidOptions.filter((option) => option.optionEvaluation);
  const allIncorrectOptions = allValidOptions.filter((option) => !option.optionEvaluation);
  const { [FeatureFlags.EnableLyearnAI]: isLyearnAIEnabled } = useFeatureFlags();

  const validateQuestionText = useCallback(() => {
    if (!question.questionText) {
      setNudgeState(t('AI:YouNeedQuestionForAiToWork'));
      return false;
    }
    return true;
  }, [question.questionText, setNudgeState, t]);

  const onShowAISuggestions = useCallback(() => {
    validateQuestionText() && openAISuggestions();
  }, [validateQuestionText, openAISuggestions]);

  const weightValidation = useMemo(() => {
    if (question.subType === QuestionSubTypeEnum.WeightedOption) {
      return validateWeightedOptions(t, options);
    }
    return null;
  }, [question.subType, options, t]);

  return (
    <Stack className={className} direction="col">
      <DraggableList
        allowDropOnStart
        classes={CLASSES}
        disabled={disableReordering}
        renderItem={(index, getDragHandleProps) => {
          const option = options[index];
          return (
            <EditorOptionsListItem
              key={`${option.tempId}_${option.optionEvaluation}_${index}`}
              disableReordering={disableReordering}
              getDragHandleProps={getDragHandleProps}
              index={index}
              isLocalizedContent={isLocalizedContent}
              option={option}
              showEvaluation={variant === 'quiz'}
              showWeight={question.subType === QuestionSubTypeEnum.WeightedOption}
              viewMode={viewMode}
              onAction={onAction}
            />
          );
        }}
        totalCount={options.length}
        onDrop={onDrop}
      />
      {weightValidation && <YellowWarning message={weightValidation.message} />}
      {viewMode !== 'create' || isLocalizedContent ? null : isLyearnAIEnabled &&
        variant === 'quiz' ? (
        <AIAddBlock onAddOption={onAddOption} onShowAISuggestions={onShowAISuggestions} />
      ) : (
        <AddBlock
          className={addOptionStyles}
          title={t('QuizCreator:AddOption')}
          onClick={onAddOption}
        />
      )}
      {showAISuggestions && isLyearnAIEnabled ? (
        <AIOptionsSuggestions
          classes={{
            open: 'mt-24',
          }}
          hideAISuggestions={closeAISuggestions}
          input={{
            question: question.questionText ?? '',
            correctOptions: allCorrectOptions.map((option) => option.optionText),
            incorrectOptions: allIncorrectOptions.map((option) => option.optionText),
          }}
          showProtip={allCorrectOptions.length < 1}
          validateQuestionText={validateQuestionText}
          onSubmit={onAddSuggestions}
        />
      ) : null}
    </Stack>
  );
}

export default EditorOptionsList;
